import type * as C from "@zooi/contracts";
import { Link } from "@tanstack/react-router";
import { dateTime } from "./format.ts";
import { ACTIONS_LABELS as L, AGENT_PLAYBOOK_LABELS as P, INBOX_LABELS as I } from "./labels.ts";
import { ActionLaunchControl } from "./local-launcher.tsx";
import { Executor } from "./ui/props.tsx";
import { StatusChip } from "./ui/status.tsx";
import { actionStatus } from "./week-items.ts";
import { executionStatus } from "./action-items.ts";

/** The proposer is never used as a substitute for the agent that claimed the work. */
export function ActionExecutor({ action }: { action: Pick<C.Action, "executor" | "execution"> }) {
  const run = action.execution?.run;
  if (action.executor === "agent" && !run) return <span className="z-muted">{L.unclaimed}</span>;
  if (!action.executor) return null;
  return <Executor kind={action.executor === "person" ? "person" : "agent"}
    engine={run?.engine} name={run?.engine ? I.engine[run.engine] ?? run.engine : L.who[action.executor]}
    where={run?.runtime ? P.runtime[run.runtime] ?? run.runtime : undefined} />;
}

export function ActionStatusChip({ action }: { action: C.Action }) {
  return <StatusChip kind={actionStatus(action)}>{action.executor === "agent" && executionStatus(action) === "approved" ? L.awaitingClaim : undefined}</StatusChip>;
}

export function ActionExecutionControls({ productId, action, compact }: { productId: string; action: C.Action; compact?: boolean }) {
  const work = action.execution;
  if (!work) return null;
  const state = executionStatus(action);
  const review = state === "stopped" || state === "failed" || state === "expired";
  if (review) return <Link id="action-execution" className="z-btn" to="/inbox/work-orders/$orderId" params={{ orderId: work.work_order_id }}>{L.reviewOrder}</Link>;
  return action.status === "committed" && work.claimable ? <ActionLaunchControl productId={productId} workOrderId={work.work_order_id} compact={compact} /> : null;
}

/** Compact progress belongs with the title's status, rather than a section in the narrative. */
export function ActionExecutionSummary({ action, zone }: { action: C.Action; zone: string }) {
  const work = action.execution;
  if (!work?.run) return null;
  const state = executionStatus(action);
  return <span className="z-action-run-summary" id={!work.claimable ? "action-execution-summary" : undefined} role="status">
    <ActionExecutor action={action} />
    <span title={L.startedAt(dateTime(work.run.started_at, zone))}>{work.run.reported_at ? L.reportedAt(dateTime(work.run.reported_at, zone)) : L.awaitingReport}</span>
    {state === "yours" && <span>{L.inputRequired}</span>}
    {work.status === "reconciling" && <span>{L.reportStopped}</span>}
  </span>;
}
